import React, { useState } from 'react';
import { X, Calendar } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const TeamsInterviewModal = ({ isOpen, onClose, candidateDetails, onInterviewScheduled }) => {
    const [formData, setFormData] = useState({
        conferenceTopic: '',
        inviteAttendees: '',
        hostName: 'Archana',
        description: '',
        date: '',
        time: '',
        timezone: 'UTC +5:30 Kolkata',
        duration: '1 hour'
    });
    const [error, setError] = useState('');

    const queryClient = useQueryClient();

    // TanStack Query mutation for scheduling Teams interview
    const scheduleInterviewMutation = useMutation({
        mutationFn: async (scheduleData) => {
            const response = await fetch('https://n8n.acespireconsulting.com/webhook/schedule-teams-interview', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(scheduleData)
            });

            if (!response.ok) {
                let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.error || errorMessage;
                } catch (e) {
                    // If we can't parse error JSON, use default message
                }
                throw new Error(errorMessage);
            }

            return response.json();
        },
        onSuccess: (result) => {
           // console.log('✅ Teams interview scheduled successfully:', result);
            
            // Handle the array response format
            const responseData = Array.isArray(result) ? result[0] : result;

            let successMessage = '';
            if (responseData && responseData.candidateData) {
                const candidateName = responseData.candidateData.candidate_name || candidateDetails.candidateName;
                const interviewType = responseData.interviewType || 'Teams Interview';
                const scheduledDateTime = responseData.scheduledDateTime;
                
                successMessage = `${interviewType} scheduled successfully for ${candidateName}! Meeting will be held on ${new Date(scheduledDateTime).toLocaleString()}.`;
                
                // console.log('📧 Meeting details:', {
                //     candidate: candidateName,
                //     type: interviewType,
                //     dateTime: scheduledDateTime,
                //     attendees: responseData.attendees
                // });
            } else {
                successMessage = `Teams interview scheduled successfully for ${candidateDetails.candidateName}!`;
            }

            // Show success message
            alert(successMessage);

            // Reset form
            setFormData({
                conferenceTopic: '',
                inviteAttendees: '',
                hostName: 'Archana',
                description: '',
                date: '',
                time: '',
                timezone: 'UTC +5:30 Kolkata',
                duration: '1 hour'
            });

            // Clear any errors
            setError('');

            // Invalidate and refetch candidate data
            queryClient.invalidateQueries({ queryKey: ['candidate', candidateDetails.candidateId] });

            // Notify parent component
            if (onInterviewScheduled) {
                onInterviewScheduled(responseData);
            }

            // Close modal after short delay to show success
            setTimeout(() => {
                onClose();
            }, 2000);
        },
        onError: (error) => {
            console.error('❌ Error scheduling Teams interview:', error);
            setError(error.message || 'Failed to schedule interview. Please try again.');
        }
    });

    const timezones = [
        'UTC +5:30 Kolkata',
        'UTC +0:00 GMT',
        'UTC -5:00 EST',
        'UTC -8:00 PST'
    ];

    const durations = [
        '30 minutes',
        '1 hour',
        '1.5 hours',
        '2 hours'
    ];

    const hosts = [
        'Archana',
        'HR Team',
        'Technical Lead',
        'Manager'
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const getDurationInMinutes = (duration) => {
        switch (duration) {
            case '30 minutes': return 30;
            case '1 hour': return 60;
            case '1.5 hours': return 90;
            case '2 hours': return 120;
            default: return 60;
        }
    };

    const formatScheduleData = () => {
        // Convert duration to minutes
        const durationMinutes = getDurationInMinutes(formData.duration);
        
        // Parse attendees (comma-separated emails)
        const attendees = formData.inviteAttendees
            .split(',')
            .map(email => email.trim())
            .filter(email => email && email.includes('@'));

        // Add default attendee if none provided
        if (attendees.length === 0) {
            attendees.push('hr@company.com');
        }

        // Format date and time
        const scheduledDate = formData.date;
        const scheduledTime = formData.time;

        return {
            candidateId: candidateDetails.candidateId.toString(),
            scheduledDate: scheduledDate,
            scheduledTime: scheduledTime,
            duration: durationMinutes,
            interviewType: formData.conferenceTopic || 'Technical Interview',
            scheduledBy: 'hr@company.com',
            timezone: 'Asia/Kolkata',
            attendees: attendees,
            meetingNotes: formData.description,
            candidateInstructions: 'Please be ready 5 minutes before the scheduled time. Ensure you have a stable internet connection.'
        };
    };

    const validateForm = () => {
        // Check required fields
        if (!formData.date || !formData.time || !formData.conferenceTopic) {
            return 'Please fill in all required fields (Conference Topic, Date, and Time)';
        }

        // Validate date is in the future
        const selectedDateTime = new Date(`${formData.date}T${formData.time}`);
        const now = new Date();
        if (selectedDateTime <= now) {
            return 'Please select a future date and time for the interview';
        }

        // Validate attendees format if provided
        if (formData.inviteAttendees) {
            const emails = formData.inviteAttendees.split(',').map(e => e.trim());
            const invalidEmails = emails.filter(email => email && !email.includes('@'));
            if (invalidEmails.length > 0) {
                return 'Please enter valid email addresses separated by commas';
            }
        }

        return null;
    };

    const handleSchedule = () => {
        setError('');

        // Validate form
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        const scheduleData = formatScheduleData();
        
        //console.log('📅 Scheduling Teams interview with data:', scheduleData);

        // Trigger the mutation
        scheduleInterviewMutation.mutate(scheduleData);
    };

    const handleDiscard = () => {
        setFormData({
            conferenceTopic: '',
            inviteAttendees: '',
            hostName: 'Archana',
            description: '',
            date: '',
            time: '',
            timezone: 'UTC +5:30 Kolkata',
            duration: '1 hour'
        });
        setError('');
        onClose();
    };

    // Get today's date in YYYY-MM-DD format for date input min attribute
    const getTodayDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-2xl mx-auto max-h-[95vh] overflow-y-auto relative">
                {/* Header */}
                <div className="flex items-center justify-between p-8 pb-6">
                    <h2 className="text-2xl font-semibold text-gray-900">Schedule an Interview</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-2"
                        disabled={scheduleInterviewMutation.isPending}
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="px-8 pb-8">
                    {/* Success Message */}
                    {scheduleInterviewMutation.isSuccess && (
                        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-green-800 text-sm">
                                ✅ Interview scheduled successfully! Closing modal...
                            </p>
                        </div>
                    )}

                    {/* Error Message */}
                    {(error || scheduleInterviewMutation.isError) && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-red-800 text-sm">
                                {error || scheduleInterviewMutation.error?.message}
                            </p>
                        </div>
                    )}

                    {/* Candidate Info */}
                    <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-blue-800 text-sm">
                            <strong>Candidate:</strong> {candidateDetails?.candidateName}<br />
                            <strong>Email:</strong> {candidateDetails?.candidateEmail}<br />
                            <strong>Position:</strong> {candidateDetails?.jobTitle}
                        </p>
                    </div>

                    {/* Form */}
                    <div className="space-y-6">
                        {/* Conference Topic */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Conference Topic
                            </label>
                            <input
                                type="text"
                                name="conferenceTopic"
                                value={formData.conferenceTopic}
                                onChange={handleInputChange}
                                placeholder="Set a conference topic"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                                disabled={scheduleInterviewMutation.isPending}
                            />
                        </div>

                        {/* Invite Attendees */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Invite Attendees
                            </label>
                            <input
                                type="text"
                                name="inviteAttendees"
                                value={formData.inviteAttendees}
                                onChange={handleInputChange}
                                placeholder="e-mail id"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                                disabled={scheduleInterviewMutation.isPending}
                            />
                        </div>

                        {/* Host name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Host name
                            </label>
                            <select
                                name="hostName"
                                value={formData.hostName}
                                onChange={handleInputChange}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base bg-white"
                                disabled={scheduleInterviewMutation.isPending}
                            >
                                {hosts.map((host) => (
                                    <option key={host} value={host}>{host}</option>
                                ))}
                            </select>
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Description
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                rows={4}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-base"
                                placeholder="Add interview description, agenda, or special instructions..."
                                disabled={scheduleInterviewMutation.isPending}
                            />
                        </div>

                        {/* Date and time */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Date and time
                            </label>
                            <div className="grid grid-cols-4 gap-3">
                                <input
                                    type="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleInputChange}
                                    min={getTodayDate()}
                                    className="px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    disabled={scheduleInterviewMutation.isPending}
                                />
                                <input
                                    type="time"
                                    name="time"
                                    value={formData.time}
                                    onChange={handleInputChange}
                                    className="px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    disabled={scheduleInterviewMutation.isPending}
                                />
                                <select
                                    name="timezone"
                                    value={formData.timezone}
                                    onChange={handleInputChange}
                                    className="px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                                    disabled={scheduleInterviewMutation.isPending}
                                >
                                    {timezones.map((tz) => (
                                        <option key={tz} value={tz}>{tz}</option>
                                    ))}
                                </select>
                                <select
                                    name="duration"
                                    value={formData.duration}
                                    onChange={handleInputChange}
                                    className="px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                                    disabled={scheduleInterviewMutation.isPending}
                                >
                                    {durations.map((duration) => (
                                        <option key={duration} value={duration}>{duration}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Set duration label */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Set duration
                            </label>
                            <div className="text-sm text-gray-500">
                                Duration is set in the time selector above
                            </div>
                        </div>
                    </div>

                    {/* Buttons */}
                    <div className="flex justify-center gap-4 mt-8 pt-6 border-t border-gray-200">
                        <button
                            onClick={handleDiscard}
                            disabled={scheduleInterviewMutation.isPending}
                            className="px-8 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors disabled:opacity-50 text-base"
                        >
                            Discard
                        </button>
                        <button
                            onClick={handleSchedule}
                            disabled={scheduleInterviewMutation.isPending}
                            className="px-8 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2 text-base"
                        >
                            {scheduleInterviewMutation.isPending ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Scheduling...
                                </>
                            ) : (
                                <>
                                    <Calendar className="w-5 h-5" />
                                    Schedule
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Loading Overlay */}
                {scheduleInterviewMutation.isPending && (
                    <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-xl">
                        <div className="text-center">
                            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                            <p className="text-base text-gray-600">Creating Teams meeting...</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TeamsInterviewModal;